import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import MultilingualInput from './MultilingualInput';
import MarkdownEditorMultilingual from './MarkdownEditorMultilingual';
import {
  AssessmentQuestion,
  AssessmentQuestionType,
  CourseAssessment,
  FollowupImpact,
  LinkedQuestion,
  LinkedQuestionType,
  buildNewAssessmentQuestion,
  buildNewFollowupImpact,
  buildNewLinkedQuestion,
  pickLocalizedTranslation
} from '../types/assessment';

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

const reorderArrayMove = <T extends { order: number }>(
  list: T[],
  from: number,
  to: number
): T[] => {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((item, idx) => ({ ...item, order: idx + 1 }));
};

const reorderRemove = <T extends { order: number }>(list: T[], index: number): T[] =>
  list.filter((_, i) => i !== index).map((item, idx) => ({ ...item, order: idx + 1 }));

const reorderAppend = <T extends { order: number }>(list: T[], item: T): T[] =>
  [...list, { ...item, order: list.length + 1 }];

const confirmAssessmentDelete = (message: string): boolean => window.confirm(message);

// --------------------------------------------------------------------------
// Sous-composant : éditeur d'options (weighted-mcq)
// --------------------------------------------------------------------------

interface WeightedMcqOptionsEditorProps {
  options: { text: { [k: string]: string }; points: number }[];
  availableLanguages: string[];
  /** Bloque ajout/suppression d'options et modification des points */
  structureLocked?: boolean;
  onChange: (options: { text: { [k: string]: string }; points: number }[]) => void;
}

const WeightedMcqOptionsEditor: React.FC<WeightedMcqOptionsEditorProps> = ({
  options,
  availableLanguages,
  structureLocked,
  onChange
}) => {
  const update = (i: number, patch: Partial<{ text: any; points: number }>) => {
    const next = options.map((o, idx) => (idx === i ? { ...o, ...patch } : o));
    onChange(next);
  };
  const remove = (i: number) => {
    if (options.length <= 2) return;
    onChange(options.filter((_, idx) => idx !== i));
  };
  const add = () => {
    onChange([...options, { text: {}, points: 0 }]);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Options (min 2)
        </Typography>
        <Button size="small" onClick={add} disabled={structureLocked}>
          Ajouter une option
        </Button>
      </Box>
      {options.map((opt, i) => (
        <Box key={i} display="flex" gap={1} alignItems="flex-start" mb={1}>
          <Box flexGrow={1}>
            <MultilingualInput
              label=""
              value={opt.text}
              onChange={(v) => update(i, { text: v })}
              availableLanguages={availableLanguages}
              required
            />
          </Box>
          <TextField
            label="Points"
            type="number"
            size="small"
            value={Number.isFinite(opt.points) ? opt.points : 0}
            onChange={(e) => update(i, { points: Number(e.target.value) })}
            sx={{ width: 100 }}
            disabled={structureLocked}
          />
          <Tooltip title={options.length <= 2 ? 'Min 2 options' : 'Supprimer cette option'}>
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (
                    !confirmAssessmentDelete(
                      'Supprimer cette option de réponse ? Cette action est irréversible.'
                    )
                  ) {
                    return;
                  }
                  remove(i);
                }}
                disabled={structureLocked || options.length <= 2}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
};

// --------------------------------------------------------------------------
// Sous-composant : éditeur d'échelle
// --------------------------------------------------------------------------

interface ScaleEditorProps {
  scale: { min: number; max: number; step?: number; minLabel?: any; maxLabel?: any };
  availableLanguages: string[];
  /** Bloque min / max / step ; les libellés restent traduisibles */
  structureLocked?: boolean;
  onChange: (scale: ScaleEditorProps['scale']) => void;
}

const ScaleEditor: React.FC<ScaleEditorProps> = ({
  scale,
  availableLanguages,
  structureLocked,
  onChange
}) => {
  return (
    <Box>
      <Stack direction="row" spacing={2} mt={1}>
        <TextField
          label="Min"
          type="number"
          size="small"
          value={scale.min ?? 1}
          onChange={(e) => onChange({ ...scale, min: Number(e.target.value) })}
          sx={{ width: 110 }}
          disabled={structureLocked}
        />
        <TextField
          label="Max"
          type="number"
          size="small"
          value={scale.max ?? 5}
          onChange={(e) => onChange({ ...scale, max: Number(e.target.value) })}
          sx={{ width: 110 }}
          disabled={structureLocked}
        />
        <TextField
          label="Pas (step)"
          type="number"
          size="small"
          value={scale.step ?? 1}
          onChange={(e) => onChange({ ...scale, step: Number(e.target.value) })}
          sx={{ width: 130 }}
          disabled={structureLocked}
        />
      </Stack>
      <Box mt={1.5}>
        <MultilingualInput
          label="Libellé minimum (optionnel)"
          value={scale.minLabel || {}}
          onChange={(v) => onChange({ ...scale, minLabel: v })}
          availableLanguages={availableLanguages}
        />
      </Box>
      <Box mt={1}>
        <MultilingualInput
          label="Libellé maximum (optionnel)"
          value={scale.maxLabel || {}}
          onChange={(v) => onChange({ ...scale, maxLabel: v })}
          availableLanguages={availableLanguages}
        />
      </Box>
    </Box>
  );
};

// --------------------------------------------------------------------------
// Sous-composant : éditeur d'une question (linked ou specific)
// --------------------------------------------------------------------------

interface QuestionEditorProps {
  index: number;
  total: number;
  question: LinkedQuestion | AssessmentQuestion;
  /** Si true, le type est restreint à 'weighted-mcq' | 'scale' (cas linked). */
  isLinked: boolean;
  /** Si true, type/points/options/échelle sont verrouillés ; les traductions restent éditables. */
  locked?: boolean;
  /** Langue préférée pour l’aperçu une ligne quand la carte est repliée. */
  previewPreferredLang?: string;
  availableLanguages: string[];
  onChange: (next: LinkedQuestion | AssessmentQuestion) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  index,
  total,
  question,
  isLinked,
  locked,
  previewPreferredLang,
  availableLanguages,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const collapsedPreview = React.useMemo(() => {
    const raw = pickLocalizedTranslation(question.question || {}, {
      preferredLang: previewPreferredLang || 'en',
      fallback: ''
    });
    const t = (raw || '').trim();
    if (!t) return '—';
    return t.length > 140 ? `${t.slice(0, 140)}…` : t;
  }, [question.question, previewPreferredLang]);

  const updateType = (type: AssessmentQuestionType | LinkedQuestionType) => {
    const next: any = { ...question, type };
    if (type === 'weighted-mcq') {
      next.options = (question as any).options || [
        { text: {}, points: 0 },
        { text: {}, points: 0 }
      ];
      next.scale = undefined;
    } else if (type === 'scale') {
      next.scale = (question as any).scale || { min: 1, max: 5 };
      next.options = undefined;
    } else {
      next.options = undefined;
      next.scale = undefined;
    }
    onChange(next);
  };

  const handleDelete = () => {
    if (locked) return;
    if (
      !confirmAssessmentDelete(
        `Supprimer la question ${index + 1} ? Cette action est irréversible.`
      )
    ) {
      return;
    }
    onDelete();
  };

  return (
    <Card
      sx={{
        mb: 1,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: locked ? 'warning.light' : 'grey.300',
        borderRadius: 2
      }}
    >
      <CardContent sx={{ '&:last-child': { pb: expanded ? 2 : 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={expanded ? 1 : 0}>
          <Box display="flex" alignItems="flex-start" gap={0.5} minWidth={0} flex={1}>
            <Tooltip title={expanded ? 'Réduire' : 'Développer'}>
              <IconButton
                size="small"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                sx={{ mt: 0.25, flexShrink: 0 }}
              >
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transition: 'transform 0.2s ease',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </IconButton>
            </Tooltip>
            <Box minWidth={0} flex={1}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Question {index + 1}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {question.type === 'weighted-mcq'
                    ? 'QCM pondéré'
                    : question.type === 'scale'
                      ? 'Échelle'
                      : 'Texte libre'}
                </Typography>
                {locked && (
                  <Chip
                    size="small"
                    color="warning"
                    icon={<LockIcon fontSize="small" />}
                    label="Structure verrouillée — traductions modifiables"
                  />
                )}
              </Box>
              {!expanded && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word'
                  }}
                >
                  {collapsedPreview}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Tooltip title="Monter">
              <span>
                <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Descendre">
              <span>
                <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1}>
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={locked ? 'Suppression refusée (réponses existantes)' : 'Supprimer'}>
              <span>
                <IconButton size="small" color="error" onClick={handleDelete} disabled={locked}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={expanded} timeout="auto">
          <Box sx={{ pt: 0.5 }}>
            <FormControl size="small" sx={{ minWidth: 220, mb: 1.5 }}>
              <InputLabel>Type de question</InputLabel>
              <Select
                label="Type de question"
                value={question.type}
                onChange={(e) => updateType(e.target.value as AssessmentQuestionType)}
                disabled={locked}
              >
                <MenuItem value="weighted-mcq">QCM pondéré</MenuItem>
                <MenuItem value="scale">Échelle</MenuItem>
                {!isLinked && <MenuItem value="open-text">Texte libre</MenuItem>}
              </Select>
            </FormControl>

            <MultilingualInput
              label="Texte de la question"
              value={question.question || {}}
              onChange={(v) => onChange({ ...question, question: v })}
              availableLanguages={availableLanguages}
              required
            />

            {question.type === 'weighted-mcq' && (
              <WeightedMcqOptionsEditor
                options={(question as any).options || []}
                availableLanguages={availableLanguages}
                structureLocked={locked}
                onChange={(options) => onChange({ ...question, options } as any)}
              />
            )}

            {question.type === 'scale' && (
              <ScaleEditor
                scale={(question as any).scale || { min: 1, max: 5 }}
                availableLanguages={availableLanguages}
                structureLocked={locked}
                onChange={(scale) => onChange({ ...question, scale } as any)}
              />
            )}

            {question.type === 'open-text' && (
              <Typography variant="caption" color="text.secondary">
                L'apprenant pourra saisir une réponse libre (max 5000 caractères).
              </Typography>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// --------------------------------------------------------------------------
// Liste de questions (linked OU specific) avec ajout/réordonnancement
// --------------------------------------------------------------------------

interface QuestionListEditorProps {
  questions: (LinkedQuestion | AssessmentQuestion)[];
  isLinked: boolean;
  /** Set des linkedKeys verrouillées (réponses existantes). Ignoré si isLinked === false. */
  lockedKeys?: Set<string>;
  availableLanguages: string[];
  /** Langue préférée pour l’aperçu une ligne des cartes repliées. */
  previewPreferredLang?: string;
  onChange: (next: any[]) => void;
  /** Permet de désactiver la suppression aussi pour les non-linked si la mesure a déjà des réponses (warning soft). */
  warnIfResponses?: boolean;
}

const QuestionListEditor: React.FC<QuestionListEditorProps> = ({
  questions,
  isLinked,
  lockedKeys,
  availableLanguages,
  previewPreferredLang,
  onChange
}) => {
  const addQuestion = () => {
    const newQ: any = isLinked
      ? buildNewLinkedQuestion(questions.length + 1, 'scale')
      : buildNewAssessmentQuestion(questions.length + 1, 'scale');
    onChange([...questions, newQ]);
  };

  return (
    <Box>
      {questions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Aucune question pour le moment.
        </Typography>
      )}
      {questions.map((q, i) => {
        const linkedKey = (q as LinkedQuestion).linkedKey;
        const locked = isLinked && !!linkedKey && !!lockedKeys?.has(linkedKey);
        const total = questions.length;
        return (
          <QuestionEditor
            key={(q as any)._id || (q as any).linkedKey || `idx-${i}`}
            index={i}
            total={total}
            question={q}
            isLinked={isLinked}
            locked={locked}
            previewPreferredLang={previewPreferredLang}
            availableLanguages={availableLanguages}
            onChange={(next) =>
              onChange(questions.map((qq, idx) => (idx === i ? next : qq)) as any[])
            }
            onMoveUp={() => onChange(reorderArrayMove(questions as any, i, i - 1))}
            onMoveDown={() => onChange(reorderArrayMove(questions as any, i, i + 1))}
            onDelete={() => onChange(reorderRemove(questions as any, i))}
          />
        );
      })}
      <Box mt={1}>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addQuestion}>
          Ajouter une question
        </Button>
      </Box>
    </Box>
  );
};

// --------------------------------------------------------------------------
// Followup impact editor
// --------------------------------------------------------------------------

interface FollowupEditorProps {
  followup: FollowupImpact;
  index: number;
  total: number;
  availableLanguages: string[];
  /** Langue par défaut du cours — utilisée pour le titre de l’accordéon. */
  defaultLanguage: string;
  onChange: (next: FollowupImpact) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  /** Désactive la suppression si réponses existent (passé par le parent). */
  deleteDisabled?: boolean;
  deleteDisabledReason?: string;
}

const FollowupEditor: React.FC<FollowupEditorProps> = ({
  followup,
  index,
  total,
  availableLanguages,
  defaultLanguage,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete,
  deleteDisabled,
  deleteDisabledReason
}) => {
  const followupLabel = pickLocalizedTranslation(followup.label, {
    preferredLang: defaultLanguage,
    fallback: `Mesure de suivi ${index + 1}`,
  });

  const handleDelete = () => {
    if (deleteDisabled) return;
    if (
      !confirmAssessmentDelete(
        `Supprimer la mesure « ${followupLabel} » ? Cette action est irréversible.`
      )
    ) {
      return;
    }
    onDelete();
  };

  return (
    <Accordion sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, width: '100%', pr: 1 }} noWrap>
          {followupLabel}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={0.5}
          sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tooltip title="Monter">
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
                <ArrowUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Descendre">
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1}>
                <ArrowDownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={deleteDisabled ? deleteDisabledReason || 'Suppression désactivée' : 'Supprimer'}
          >
            <span>
              <IconButton size="small" color="error" onClick={handleDelete} disabled={deleteDisabled}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <MultilingualInput
          label="Libellé de la mesure"
          value={followup.label || {}}
          onChange={(v) => onChange({ ...followup, label: v })}
          availableLanguages={availableLanguages}
          required
        />
        <Box mt={1.5}>
          <MarkdownEditorMultilingual
            label="Description (optionnelle)"
            value={followup.description || {}}
            onChange={(v) => onChange({ ...followup, description: v })}
            availableLanguages={availableLanguages}
            placeholder="Texte d’introduction affiché avant le questionnaire (mise en forme riche)"
            height={280}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Questions spécifiques à cette mesure
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Les questions liées (définies plus haut) seront automatiquement présentées en
          premier dans cette mesure.
        </Typography>
        <QuestionListEditor
          questions={followup.questions || []}
          isLinked={false}
          availableLanguages={availableLanguages}
          previewPreferredLang={defaultLanguage}
          onChange={(qs) => onChange({ ...followup, questions: qs as AssessmentQuestion[] })}
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          La date de déblocage de cette mesure se règle <strong>par groupe</strong> dans
          l'onglet « Groupes ».
        </Alert>
      </AccordionDetails>
    </Accordion>
  );
};

// --------------------------------------------------------------------------
// Composant principal
// --------------------------------------------------------------------------

export interface AssessmentEditorProps {
  enabled: boolean;
  onToggleEnabled: (next: boolean) => void;
  assessment: CourseAssessment;
  onChange: (next: CourseAssessment) => void;
  availableLanguages: string[];
  /** Langue par défaut du cours (libellés des mesures de suivi). */
  defaultLanguage: string;
  /** IDs de followups (string) pour lesquels la suppression doit être désactivée (réponses existent côté backend). */
  followupIdsWithResponses?: Set<string>;
}

const AssessmentEditor: React.FC<AssessmentEditorProps> = ({
  enabled,
  onToggleEnabled,
  assessment,
  onChange,
  availableLanguages,
  defaultLanguage,
  followupIdsWithResponses
}) => {
  const lockedKeys = React.useMemo(() => {
    const set = new Set<string>();
    for (const lq of assessment.linkedQuestions || []) {
      if (lq.hasResponses && lq.linkedKey) set.add(lq.linkedKey);
    }
    return set;
  }, [assessment.linkedQuestions]);

  const updateLinkedQuestions = (qs: any[]) => onChange({ ...assessment, linkedQuestions: qs });
  const updateInitialQuestions = (qs: any[]) =>
    onChange({ ...assessment, initialDiagnosticQuestions: qs });
  const updateFinalQuestions = (qs: any[]) =>
    onChange({ ...assessment, finalImpactQuestions: qs });

  const setFollowups = (next: FollowupImpact[]) => onChange({ ...assessment, followupImpacts: next });

  return (
    <Box>
      {/* Toggle principal */}
      <Card sx={{ mb: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Diagnostic & mesure d'impact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Activez cette feature pour proposer un diagnostic initial, une mesure d'impact
                en fin de formation, et des mesures de suivi à dates programmées.
              </Typography>
            </Box>
            <Button
              variant={enabled ? 'outlined' : 'contained'}
              color={enabled ? 'warning' : 'primary'}
              onClick={() => onToggleEnabled(!enabled)}
            >
              {enabled ? 'Désactiver' : 'Activer'}
            </Button>
          </Box>
          {!enabled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              La feature est désactivée. La configuration ci-dessous est conservée mais aucun
              questionnaire n'est exposé aux apprenants.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Box sx={{ opacity: enabled ? 1 : 0.5, pointerEvents: enabled ? 'auto' : 'none' }}>
        {/* Questions liées */}
        <Accordion
          defaultExpanded={false}
          disableGutters
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div">
            <Typography variant="h6" fontWeight={700}>
              Questions liées
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Présentes dans <strong>toutes</strong> les mesures (diagnostic, mesure finale,
              mesures de suivi), affichées en premier. Permettent de mesurer l'évolution dans
              le temps. Types autorisés : QCM pondéré et échelle.
            </Typography>
            <QuestionListEditor
              questions={assessment.linkedQuestions || []}
              isLinked
              lockedKeys={lockedKeys}
              availableLanguages={availableLanguages}
              previewPreferredLang={defaultLanguage}
              onChange={updateLinkedQuestions}
            />
          </AccordionDetails>
        </Accordion>

        {/* Diagnostic initial */}
        <Accordion
          defaultExpanded={false}
          disableGutters
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div">
            <Typography variant="h6" fontWeight={700}>
              Diagnostic initial
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MarkdownEditorMultilingual
              label="Description introductive (optionnelle)"
              value={assessment.initialDiagnosticDescription || {}}
              onChange={(v) => onChange({ ...assessment, initialDiagnosticDescription: v })}
              availableLanguages={availableLanguages}
              placeholder="Texte d’introduction affiché avant le questionnaire (mise en forme riche)"
              height={320}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Questions spécifiques au diagnostic
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Affichées après les questions liées.
            </Typography>
            <QuestionListEditor
              questions={assessment.initialDiagnosticQuestions || []}
              isLinked={false}
              availableLanguages={availableLanguages}
              previewPreferredLang={defaultLanguage}
              onChange={updateInitialQuestions}
            />
          </AccordionDetails>
        </Accordion>

        {/* Mesure d'impact finale */}
        <Accordion
          defaultExpanded={false}
          disableGutters
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div">
            <Typography variant="h6" fontWeight={700}>
              Mesure d'impact finale
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MarkdownEditorMultilingual
              label="Description introductive (optionnelle)"
              value={assessment.finalImpactDescription || {}}
              onChange={(v) => onChange({ ...assessment, finalImpactDescription: v })}
              availableLanguages={availableLanguages}
              placeholder="Texte d’introduction affiché avant le questionnaire (mise en forme riche)"
              height={320}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Questions spécifiques à la mesure d'impact finale
            </Typography>
            <QuestionListEditor
              questions={assessment.finalImpactQuestions || []}
              isLinked={false}
              availableLanguages={availableLanguages}
              previewPreferredLang={defaultLanguage}
              onChange={updateFinalQuestions}
            />
          </AccordionDetails>
        </Accordion>

        {/* Followups */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight={700}>
                Mesures d'impact de suivi
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setFollowups(
                    reorderAppend(
                      assessment.followupImpacts || [],
                      buildNewFollowupImpact((assessment.followupImpacts?.length || 0) + 1)
                    )
                  )
                }
              >
                Ajouter une mesure de suivi
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Optionnelles. Reprennent automatiquement les <strong>questions liées</strong>,
              et peuvent contenir des questions spécifiques. Les dates de déblocage se règlent
              par groupe.
            </Typography>
            {(assessment.followupImpacts || []).map((f, i) => {
              const fid = f._id ? String(f._id) : '';
              const hasResp = !!fid && !!followupIdsWithResponses?.has(fid);
              return (
                <FollowupEditor
                  key={fid || `new-${i}`}
                  followup={f}
                  index={i}
                  total={(assessment.followupImpacts || []).length}
                  availableLanguages={availableLanguages}
                  defaultLanguage={defaultLanguage}
                  onChange={(next) =>
                    setFollowups(
                      (assessment.followupImpacts || []).map((ff, idx) => (idx === i ? next : ff))
                    )
                  }
                  onMoveUp={() =>
                    setFollowups(reorderArrayMove(assessment.followupImpacts || [], i, i - 1))
                  }
                  onMoveDown={() =>
                    setFollowups(reorderArrayMove(assessment.followupImpacts || [], i, i + 1))
                  }
                  onDelete={() => setFollowups(reorderRemove(assessment.followupImpacts || [], i))}
                  deleteDisabled={hasResp}
                  deleteDisabledReason="Des réponses ont déjà été soumises pour cette mesure."
                />
              );
            })}
            {(assessment.followupImpacts || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Aucune mesure de suivi configurée.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AssessmentEditor;
